import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import SiteShell from '../components/site/SiteShell'
import { deletionErrorKey, isE164 } from '../lib/deleteAccount'
import { OTP_LENGTH, codeComplete, normalizeCode, normalizePhone } from '../lib/otp'
import { deleteAccountByPhone, deleteOwnAccount } from '../services/account'
import { requestOtp } from '../services/auth'
import { useAuthStore } from '../store/authStore'

/**
 * Account deletion (`/delete-account`, tooti#150) — public, no session
 * required: Google Play asks for a web page where anyone can delete their
 * account without the app, and its Data safety form links here.
 *
 * Two steps, both on the page: the number gets a one-time code (the same
 * `auth_otpRequest` as sign-in), then the code plus an explicit "I
 * understand" confirm the deletion. A visitor who is signed in deletes their
 * own account with `account_delete` (the number is theirs, read-only) and is
 * signed out; anyone else goes through `account_deleteByPhone`. Deletion is
 * full and immediate (owner, 2026-09-26) — the list on the page says what
 * goes, in the same words as the privacy policy.
 */

const CARD = 'flex flex-col gap-4 rounded-[28px] border-2 border-line bg-surface p-5 shadow-[0_8px_0_var(--color-line)] sm:p-7'
const FIELD =
  'h-[58px] rounded-2xl border-2 border-line-2 bg-ground px-4 text-[15px] text-ink outline-none transition placeholder:text-ink-3 focus:border-brand-500'
const LABEL = 'text-[13.5px] font-bold text-ink-2'
const PRIMARY =
  'site-btn flex h-[60px] items-center justify-center rounded-[18px] font-display text-[19px] font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-60'

export default function DeleteAccount() {
  const { t } = useTranslation('deleteAccount')
  const status = useAuthStore((s) => s.status)
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const signedInPhone = status === 'authenticated' ? (user?.phone ?? null) : null

  const [step, setStep] = useState('phone') // 'phone' | 'code' | 'done'
  const [phoneInput, setPhoneInput] = useState('')
  const [code, setCode] = useState('')
  const [understood, setUnderstood] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [deleted, setDeleted] = useState(null)
  // The number the code went to; fixed once sent, so a sign-out mid-flow cannot change it.
  const [sentTo, setSentTo] = useState(null)

  const what = t('what', { returnObjects: true })
  const phone = signedInPhone ?? normalizePhone(phoneInput)

  async function sendCode(event) {
    event.preventDefault()
    setError(null)
    if (!isE164(phone)) {
      setError('errors.phoneInvalid')
      return
    }
    setBusy(true)
    try {
      await requestOtp(phone)
      setSentTo({ phone, own: Boolean(signedInPhone) })
      setCode('')
      setUnderstood(false)
      setStep('code')
    } catch (failure) {
      setError(deletionErrorKey(failure))
    } finally {
      setBusy(false)
    }
  }

  async function confirm(event) {
    event.preventDefault()
    if (!codeComplete(code) || !understood || !sentTo) return
    setError(null)
    setBusy(true)
    try {
      const result = sentTo.own ? await deleteOwnAccount(code) : await deleteAccountByPhone(sentTo.phone, code)
      if (sentTo.own) signOut()
      setDeleted(result?.deleted !== false)
      setStep('done')
    } catch (failure) {
      setError(deletionErrorKey(failure))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SiteShell title={t('pageTitle')}>
      <div className="mx-auto flex w-full max-w-[800px] flex-col gap-[26px] px-4 pb-[clamp(44px,8vw,72px)] pt-[clamp(30px,6vw,54px)] sm:px-5">
        <header className="flex flex-col gap-3.5" style={{ animation: 't-rise .5s ease-out both' }}>
          <span className="self-start rounded-full bg-bad-tint px-4 py-2 font-display text-[15px] font-extrabold text-bad-ink">{t('badge')}</span>
          <h1 className="font-display text-[clamp(30px,6.4vw,44px)] font-extrabold leading-[1.15] tracking-[-1.3px]">{t('title')}</h1>
          <p className="text-base leading-[1.85] text-ink-2 [text-wrap:pretty]">{t('lead')}</p>
        </header>

        {step === 'done' ? (
          <section role="status" className={CARD} style={{ animation: 't-pop .4s ease-out both' }}>
            <h2 className="font-display text-xl font-extrabold">{deleted ? t('doneTitle') : t('noAccountTitle')}</h2>
            <p className="text-[15px] leading-[1.85] text-ink-2">{deleted ? t('doneBody') : t('noAccountBody')}</p>
            <Link
              to="/"
              className="site-btn inline-flex h-[52px] items-center justify-center self-start rounded-2xl bg-surface-2 px-[22px] font-display text-base font-extrabold text-ink [--press:var(--color-line-2)]"
            >
              {t('home')}
            </Link>
          </section>
        ) : (
          <>
            <section aria-labelledby="delete-what" className="flex flex-col gap-3 rounded-[22px] border-2 border-line bg-surface px-[22px] py-5">
              <h2 id="delete-what" className="font-display text-xl font-extrabold">
                {t('whatTitle')}
              </h2>
              <ul className="flex list-disc flex-col gap-1.5 ps-5 text-[15px] leading-[1.8] text-ink-2">
                {what.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="rounded-[16px] bg-bad-tint px-4 py-3 text-[14.5px] font-bold leading-[1.8] text-bad-ink">{t('permanent')}</p>
            </section>

            {step === 'phone' ? (
              <form onSubmit={sendCode} className={CARD} noValidate>
                {signedInPhone ? (
                  <p className="text-[15px] text-ink-2">
                    {t('signedInAs')}{' '}
                    <span dir="ltr" className="font-bold text-ink">
                      {signedInPhone}
                    </span>
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label htmlFor="delete-phone" className={LABEL}>
                      {t('phoneLabel')}
                    </label>
                    <input
                      id="delete-phone"
                      dir="ltr"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phoneInput}
                      onChange={(event) => setPhoneInput(event.target.value)}
                      placeholder={t('phonePlaceholder')}
                      aria-describedby="delete-phone-hint"
                      className={`${FIELD} text-start`}
                    />
                    <span id="delete-phone-hint" className="text-[13px] leading-[1.8] text-ink-2">
                      {t('phoneHint')}
                    </span>
                  </div>
                )}
                <button type="submit" disabled={busy} className={`${PRIMARY} bg-brand-600 [--press:var(--color-brand-deep)] dark:text-slate-900`}>
                  {busy ? t('sending') : t('sendCode')}
                </button>
              </form>
            ) : (
              <form onSubmit={confirm} className={CARD} noValidate>
                <p className="text-[15px] text-ink-2">
                  {t('codeSent')}{' '}
                  <span dir="ltr" className="font-bold text-ink">
                    {sentTo?.phone}
                  </span>
                </p>
                <div className="flex flex-col gap-2">
                  <label htmlFor="delete-code" className={LABEL}>
                    {t('codeLabel')}
                  </label>
                  <input
                    id="delete-code"
                    dir="ltr"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={OTP_LENGTH}
                    value={code}
                    onChange={(event) => setCode(normalizeCode(event.target.value))}
                    className={`${FIELD} tnum text-center text-[22px] tracking-[0.4em]`}
                  />
                </div>
                <label className="flex items-start gap-3 text-[14.5px] leading-[1.8] text-ink">
                  <input
                    type="checkbox"
                    checked={understood}
                    onChange={(event) => setUnderstood(event.target.checked)}
                    className="mt-1.5 size-5 shrink-0 accent-[var(--color-bad)]"
                  />
                  <span>{t('understand')}</span>
                </label>
                <button
                  type="submit"
                  disabled={busy || !codeComplete(code) || !understood}
                  className={`${PRIMARY} bg-bad [--press:var(--color-bad-ink)]`}
                >
                  {busy ? t('deleting') : t('confirm')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone')
                    setError(null)
                  }}
                  className="self-start text-sm font-bold text-brand-600 hover:underline"
                >
                  {t('changePhone')}
                </button>
              </form>
            )}

            {error && (
              <p role="alert" className="rounded-[16px] bg-bad-tint px-4 py-3 text-[14.5px] font-bold leading-[1.8] text-bad-ink">
                {t(error)}
              </p>
            )}
          </>
        )}

        <Link to="/privacy#privacy-rights" className="self-start text-sm font-bold text-brand-600 hover:underline">
          {t('privacyLink')}
        </Link>
      </div>
    </SiteShell>
  )
}
